import { useMemo } from 'react'
import { useAppState } from '../../../appState'
import { SbSettingToggleProps } from './SbSettingToggle'
import { SbFolder } from './type'

interface UserProfile {
  userName?: string
  canCreateForwardableBoards?: boolean
}

export default function useSbSettings(folder: SbFolder, username?: string) {
  const [appState] = useAppState() as any

  const userProfile = (appState?.userProfile || {}) as UserProfile

  const canEdit = userProfile?.userName === username

  const canForward = userProfile?.canCreateForwardableBoards

  const canToggleFeatured = Boolean(
    userProfile?.canCreateForwardableBoards === true &&
      (folder.totalFeatured < 4 || folder.isFeatured)
  )

  const canToggleImage = canForward || !!folder.displayImage

  const settings = useMemo(() => {
    const availableSettings: Pick<
      SbSettingToggleProps,
      'field' | 'isChecked' | 'tooltip' | 'label' | 'disabled'
    >[] = [
      {
        field: 'isPublic',
        label: 'is public',
        isChecked: folder.isPublic,
        tooltip: 'enabling this option will make this folder publicly visible',
      },
    ]

    if (canToggleFeatured) {
      availableSettings.push({
        field: 'isFeatured',
        label: 'featured',
        isChecked: folder.isFeatured,
        tooltip:
          'enabling this option will add this folder to the featured section on the folders listing page. for this to work the folder needs to be a public.',
        disabled: !folder.isPublic,
      })
    }

    if (canForward) {
      availableSettings.push({
        field: 'isForwardable',
        label: 'forwardable',
        isChecked: folder.isForwardable,
        tooltip:
          'enabling this option enables other users to import this shopping board to their accounts',
      })
    }

    if (canToggleImage) {
      availableSettings.push({
        field: 'isDisplayImageVisible',
        label: 'show image',
        isChecked: folder.isDisplayImageVisible,
        tooltip:
          "enabling this option will show folder's featured image (if any) on the items listing",
      })
    }

    return availableSettings
  }, [
    canForward,
    canToggleFeatured,
    folder.isPublic,
    folder.isForwardable,
    folder.isFeatured,
    folder.isDisplayImageVisible,
  ])

  return { canEdit, canForward, canToggleFeatured, settings }
}
